import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const authMethodTarget = {
  kind: "configuration" as const,
  field: "authMethod",
} as const;

export const alertLogic = defineConnector({
  identifier: "alertLogic",
  name: "Alert Logic",
  tagLine: "Managed detection and response",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ apiAddress }) =>
    apiAddress
      ? `${apiAddress.replace(/\./g, "\\.").replace(/\/$/, "")}/.*`
      : "https://api\\.[a-z0-9.-]+alertlogic\\.(com|co\\.uk)/.*",
  auth: (app) => {
    const base = (app.configuration.apiAddress ?? "").replace(/\/$/, "");
    const isKey = app.configuration.authMethod !== "usernamePassword";
    return fetchTokenAuth({
      request: {
        url: `${base}/aims/v1/authenticate`,
        method: "POST",
        basicAuth: isKey
          ? {
              username: app.config.keyId,
              password: app.secret.keySecret,
            }
          : {
              username: app.config.username,
              password: app.secret.password,
            },
      },
      extract: { from: "jsonPath", paths: ["authentication.token"] },
      apply: { kind: "header", name: "x-aims-auth-token" },
    });
  },
  steps: [
    {
      title: "Choose an authentication method",
      items: [
        {
          kind: "select",
          label: "Authentication method",
          required: true,
          defaultValue: "apiKey",
          target: authMethodTarget,
          options: [
            { value: "apiKey", label: "API key" },
            { value: "usernamePassword", label: "Username & password" },
          ],
        },
      ],
    },
    {
      title: "Enter your API details",
      items: [
        {
          kind: "url",
          label: "API address",
          placeholder: "https://api.global-services.global.alertlogic.com",
          required: true,
          defaultValue: "https://api.global-services.global.alertlogic.com",
          target: { kind: "configuration", field: "apiAddress" },
          helperText: "Your Alert Logic regional API base URL.",
        },
        {
          kind: "input",
          label: "Key ID",
          required: true,
          target: { kind: "configuration", field: "keyId" },
          visibleWhen: { target: authMethodTarget, value: "apiKey" },
        },
        {
          kind: "input",
          label: "Key secret",
          required: true,
          target: { kind: "secrets", field: "keySecret" },
          visibleWhen: { target: authMethodTarget, value: "apiKey" },
        },
        {
          kind: "input",
          label: "Username",
          required: true,
          target: { kind: "configuration", field: "username" },
          visibleWhen: {
            target: authMethodTarget,
            value: "usernamePassword",
          },
        },
        {
          kind: "input",
          label: "Password",
          required: true,
          target: { kind: "secrets", field: "password" },
          visibleWhen: {
            target: authMethodTarget,
            value: "usernamePassword",
          },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ apiAddress }) =>
      `${(apiAddress ?? "").replace(/\/$/, "")}/aims/v1/token_info`,
  },
});
